import {useQuery} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {Strong} from '@/components/shared/Strong'
import {Stack} from '@/components/shared/Stack'
import {useParams} from 'react-router-dom'
import {RefreshCw, History, ChevronDown, ChevronRight, Filter} from 'lucide-react'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Button} from '@/components/ui/button'
import {ClearFiltersButton} from '@/components/shared/ClearFiltersButton'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {Card, CardHeader, CardTitle, CardDescription, CardContent} from '@/components/ui/card'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {routes} from '@/lib/routes'
import {notFoundIfMissing} from '@/lib/not-found-error'
import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import {useState, useMemo} from 'react'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {formatDateOnly, formatTimestamp, formatRelativeTime} from '@/lib/date-utils'
import {PageLayout} from '@/components/shared/PageLayout'
import {TextLink} from '@/components/shared/TextLink'
import {EntityLink as EntityLinkChip} from '@/components/shared/EntityLink'
import {MutedText} from '@/components/shared/MutedText'
import {Icon} from '@/components/shared/Icon'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {InlineRow} from '@/components/shared/InlineRow'
import {SplitRow} from '@/components/shared/SplitRow'
import {CompactButton} from '@/components/shared/CompactButton'
import {InfoBanner} from '@/components/shared/InfoBanner'

export function IdentityGrantsDetail() {
    const services = useServices()
    const i18n = useI18n()
    usePageTitle(i18n.analytics.identityGrantsPageTitle)
    const {id} = useParams<{ id: string }>()
    const {hasPermission} = useAuth()
    const [expandedGrants, setExpandedGrants] = useState<Set<string>>(new Set())
    const [recalculateMessage, setRecalculateMessage] = useState<string | null>(null)

    const canRecalculate = hasPermission(permissions.effectiveAccess.recalculate)
    const {identityTypes, lifecycleStates} = useAppConfig()

    // Filter state
     
    const ALL_FILTER = 'all'
    const [environmentFilter, setEnvironmentFilter] = useState<string>(ALL_FILTER)
    const [systemFilter, setSystemFilter] = useState<string>(ALL_FILTER)

    // Fetch identity
    const {data: identity, isLoading: identityLoading, error: identityError} = useQuery({
        queryKey: ['identities', id],
        queryFn: () => services.identities.detail(id!),
        enabled: !!id,
    })

    // Fetch grants
    const {data: grants, isLoading: grantsLoading} = useQuery({
        queryKey: ['identity-grants', id],
        queryFn: () => services.identities.grants(id!),
        enabled: !!id,
    })

    // Fetch stats
    const {data: stats} = useQuery({
        queryKey: ['identity-grants-stats', id],
        queryFn: () => services.identities.grantStats(id!),
        enabled: !!id,
    })

    // Recalculate mutation
    const recalculateMutation = useEntityMutation({
        mutationFn: () => services.identities.recalculateGrants(id!),
        invalidate: [['identity-grants', id], ['identity-grants-stats', id]],
        onSuccess: () => {
            setRecalculateMessage(i18n.analytics.recalculateRequested as string)
            setTimeout(() => setRecalculateMessage(null), 5000)
        },
        onError: () => {
            setRecalculateMessage(i18n.analytics.recalculateFailed as string)
            setTimeout(() => setRecalculateMessage(null), 5000)
        },
    })

    const toggleGrant = (grantId: string) => {
        const newExpanded = new Set(expandedGrants)
        if (newExpanded.has(grantId)) {
            newExpanded.delete(grantId)
        } else {
            newExpanded.add(grantId)
        }
        setExpandedGrants(newExpanded)
    }

    // Extract unique environments and systems for filters
    const environments = useMemo(() => {
        if (!grants) return []
        const envSet = new Set(grants.map(g => g.targetSystem.subType).filter((v): v is string => v != null))
        return Array.from(envSet).sort()
    }, [grants])

    const systems = useMemo(() => {
        if (!grants) return []
        const sysMap = new Map<string, string>()
        grants.forEach(g => sysMap.set(g.targetSystem.id, g.targetSystem.displayName))
        return Array.from(sysMap.entries()).sort((a, b) => a[1].localeCompare(b[1]))
    }, [grants])

    // Apply filters
    const filteredGrants = useMemo(() => {
        if (!grants) return []
        return grants.filter(g => {
            if (environmentFilter !== ALL_FILTER && g.targetSystem.subType !== environmentFilter) return false
            if (systemFilter !== ALL_FILTER && g.targetSystem.id !== systemFilter) return false
            return true
        })
    }, [grants, environmentFilter, systemFilter])

    const hasActiveFilters = environmentFilter !== ALL_FILTER || systemFilter !== ALL_FILTER

    const clearFilters = () => {
        setEnvironmentFilter(ALL_FILTER)
        setSystemFilter(ALL_FILTER)
    }

    return (
        <PageLayout
            data-testid="identity-grants-detail-page"
            variant="detail"
            title={identity ? `${i18n.analytics.effectiveAccessLabel} ${identity.party.displayName || identity.identityType}` : i18n.analytics.identityGrantsPageTitle}
            icon={CONCEPT_ICON.accessGrant}
            loading={identityLoading || grantsLoading}
            error={identityError ?? (!identityLoading && !grantsLoading ? notFoundIfMissing(identity, 'IDENTITY_GRANTS') : undefined)}
            backTo={routes.effectiveAccess.identities.list()}
            description={identity ? (
                <MutedText>{labelOf(identityTypes, identity.identityType)} - {labelOf(lifecycleStates, identity.lifecycleState)}</MutedText>
            ) : undefined}
            actions={canRecalculate ? (
                <Button
                    data-testid="btn-recalculate"
                    onClick={() => recalculateMutation.mutate()}
                    disabled={recalculateMutation.isPending}
                >
                    <Icon icon={RefreshCw} spin={recalculateMutation.isPending} className="mr-2"/>
                    {i18n.analytics.recalculate}
                </Button>
            ) : undefined}
        >
            {identity && (<>
            {/* Recalculate message */}
            {recalculateMessage && (
                <InfoBanner
                    variant={recalculateMessage.includes('failed') ? 'destructive' : 'success'}
                    className="mb-4"
                >
                    {recalculateMessage}
                </InfoBanner>
            )}

            {/* Stats Card */}
            <DetailGrid columns={3} className="mb-6">
                <Card>
                    <CardHeader className="pb-2">
                        <CardDescription>{i18n.analytics.totalGrants}</CardDescription>
                        <CardTitle className="text-2xl" data-testid="card-title-total-grants">{stats?.grantCount || grants?.length || 0}</CardTitle>
                    </CardHeader>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardDescription>{i18n.analytics.activeGrants}</CardDescription>
                        <CardTitle className="text-2xl" data-testid="card-title-active-grants">
                            {grants?.filter(g => g.active).length || 0}
                        </CardTitle>
                    </CardHeader>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardDescription>{i18n.analytics.lastCalculated}</CardDescription>
                        <CardTitle className="text-lg" data-testid="card-title-last-calculated" data-empty={!stats?.lastCalculatedAt}>
                            {stats?.lastCalculatedAt
                                ? formatRelativeTime(stats.lastCalculatedAt)
                                : i18n.analytics.never}
                        </CardTitle>
                    </CardHeader>
                </Card>
            </DetailGrid>

            {/* Grants Table */}
            <Card>
                <CardHeader>
                    <SplitRow>
                        <div>
                            <CardTitle data-testid="card-title-effective-access-grants">{i18n.analytics.effectiveAccessGrants}</CardTitle>
                            <CardDescription>
                                {`${filteredGrants.length} ${i18n.analytics.grantsFiltered({total: String(grants?.length || 0)})}${hasActiveFilters ? ` ${i18n.analytics.filtered}` : ''}`}
                            </CardDescription>
                        </div>
                        <InlineRow wrap>
                            <Icon icon={Filter} size="md" muted/>
                            <Select value={environmentFilter} onValueChange={setEnvironmentFilter}>
                                <SelectTrigger className="w-[140px]" data-testid="filter-environment">
                                    <SelectValue placeholder={i18n.analytics.environment as string}/>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{i18n.analytics.allEnvironments}</SelectItem>
                                    {environments.map(env => (
                                        <SelectItem key={env} value={env}>{env}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Select value={systemFilter} onValueChange={setSystemFilter}>
                                <SelectTrigger className="w-[180px]" data-testid="filter-system">
                                    <SelectValue placeholder={i18n.analytics.system as string}/>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{i18n.analytics.allSystems}</SelectItem>
                                    {systems.map(([sysId, sysName]) => (
                                        <SelectItem key={sysId} value={sysId}>{sysName}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <ClearFiltersButton hasActiveFilters={!!hasActiveFilters} onClick={clearFilters}/>
                            <TextLink
                                testId="link-identity-details"
                                to={routes.identityManagement.identities.detail(id!)}
                            >
                                {i18n.analytics.viewIdentityDetails}
                            </TextLink>
                        </InlineRow>
                    </SplitRow>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-8" data-testid="col-expand"></TableHead>
                                <TableHead data-testid="col-enforcement-object">{i18n.analytics.enforcementObject}</TableHead>
                                <TableHead data-testid="col-target-system">{i18n.analytics.targetSystem}</TableHead>
                                <TableHead data-testid="col-environment">{i18n.analytics.environment}</TableHead>
                                <TableHead data-testid="col-resource">{i18n.common.resource}</TableHead>
                                <TableHead data-testid="col-operation">{i18n.common.operation}</TableHead>
                                <TableHead data-testid="col-valid-from">{i18n.common.validFrom}</TableHead>
                                <TableHead data-testid="col-valid-to">{i18n.analytics.validTo}</TableHead>
                                <TableHead data-testid="col-status">{i18n.common.status}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredGrants.map((grant) => (
                                <Collapsible key={grant.accessId} asChild open={expandedGrants.has(grant.accessId)}>
                                    <>
                                        <TableRow
                                            className="cursor-pointer hover:bg-muted/50"
                                            onClick={() => toggleGrant(grant.accessId)}
                                        >
                                            <TableCell>
                                                <CollapsibleTrigger asChild>
                                                    <CompactButton variant="ghost" size="xs" data-testid={`btn-expand-grant-${grant.accessId}`}>
                                                        {expandedGrants.has(grant.accessId)
                                                            ? <Icon icon={ChevronDown} size="md"/>
                                                            : <Icon icon={ChevronRight} size="md"/>
                                                        }
                                                    </CompactButton>
                                                </CollapsibleTrigger>
                                            </TableCell>
                                            <TableCell className="font-medium">
                                                <EntityLinkChip
                                                    value={grant.enforcementObject}
                                                    testId={`link-enforcement-object-${grant.accessId}`}
                                                    stopPropagation
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <EntityLinkChip
                                                    value={grant.targetSystem}
                                                    testId={`link-target-system-${grant.accessId}`}
                                                    stopPropagation
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="secondary">{grant.targetSystem.subType ?? '—'}</Badge>
                                            </TableCell>
                                            <TableCell>
                                                {grant.resource ? (
                                                    <EntityLinkChip
                                                        value={grant.resource}
                                                        testId={`link-resource-${grant.accessId}`}
                                                        stopPropagation
                                                    />
                                                ) : (
                                                    <MutedText>-</MutedText>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline">{grant.operation}</Badge>
                                            </TableCell>
                                            <TableCell>
                                                {formatDateOnly(grant.validFrom)}
                                            </TableCell>
                                            <TableCell>
                                                {grant.permanent ? (
                                                    <Badge variant="secondary">{i18n.common.permanent}</Badge>
                                                ) : grant.validTo ? (
                                                    formatDateOnly(grant.validTo)
                                                ) : (
                                                    '-'
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={grant.active ? BadgeVariant.SUCCESS : BadgeVariant.SECONDARY}>
                                                    {grant.active ? i18n.common.active : i18n.common.inactive}
                                                </Badge>
                                            </TableCell>
                                        </TableRow>
                                        <CollapsibleContent asChild>
                                            <TableRow className="bg-muted/30">
                                                <TableCell colSpan={9} className="p-4">
                                                    <Stack gap="sm" className="text-sm">
                                                        <Strong className="mb-2">
                                                            <InlineRow gap="sm">
                                                                <Icon icon={History} size="md"/>
                                                                {i18n.analytics.sources} ({grant.sources.length})
                                                            </InlineRow>
                                                        </Strong>
                                                        <Table>
                                                            <TableHeader>
                                                                <TableRow>
                                                                    <TableHead data-testid="col-source-access-profile">{i18n.analytics.accessProfile}</TableHead>
                                                                    <TableHead data-testid="col-source-assignment-start">{i18n.analytics.assignmentStart}</TableHead>
                                                                    <TableHead data-testid="col-source-assignment-expires">{i18n.analytics.assignmentExpires}</TableHead>
                                                                    <TableHead data-testid="col-source-created-at">{i18n.common.createdAt}</TableHead>
                                                                </TableRow>
                                                            </TableHeader>
                                                            <TableBody>
                                                                {grant.sources.map((source) => (
                                                                    <TableRow key={source.sourceId}>
                                                                        <TableCell>
                                                                            <EntityLinkChip
                                                                                value={source.accessProfile}
                                                                                testId={`link-access-profile-${source.sourceId}`}
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {formatTimestamp(source.assignmentStartAt)}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {source.assignmentPermanent ? (
                                                                                <Badge
                                                                                    variant="secondary">{i18n.common.permanent}</Badge>
                                                                            ) : source.assignmentExpiresAt ? (
                                                                                formatTimestamp(source.assignmentExpiresAt)
                                                                            ) : (
                                                                                '-'
                                                                            )}
                                                                        </TableCell>
                                                                        <TableCell className="text-muted-foreground">
                                                                            {formatRelativeTime(source.createdAt)}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        </CollapsibleContent>
                                    </>
                                </Collapsible>
                            ))}
                            {filteredGrants.length === 0 && (
                                <TableEmptyState
                                    colSpan={9}
                                    hasFilters={hasActiveFilters}
                                    matchMessage={i18n.analytics.noGrantsMatchFilters}
                                    emptyMessage={i18n.analytics.noGrantsForIdentity}
                                />
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
            </>)}
        </PageLayout>
    )
}
